/**
 * EntityPickerStep - Step unificado para selecionar/criar entidades (profissionais, serviços, locais).
 *
 * Substitui ProfessionalPickerStep, ServicePickerStep e PlacePickerStep com um unico
 * componente parametrizado por entityType. Adapta titulos e subtitulos ao contexto
 * ja construido nos steps anteriores.
 *
 * Modos: create-only, select-only, select-or-create
 */

import { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, TextField, List, ListItem, ListItemText, ListItemIcon,
  Checkbox, InputAdornment, IconButton, Alert, Chip,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { buildContextSubtitle } from '../../../utils/wizardContextBuilder';
import { pluralizeWithCount, pluralizePastParticiple } from '../../../utils/pluralize';

/**
 * Mapa de configuracao por tipo de entidade.
 * Centraliza as diferencas entre professional, service e place
 * sem usar if/else espalhados pelo componente.
 */
const ENTITY_CONFIG = {
  professional: {
    getItems: (tree) => tree.professionals,
    add: (entityTree, name) => {
      const id = entityTree.addProfessional(name);
      if (id) entityTree.toggleProfessional(id);
      return id;
    },
    toggle: (entityTree, item) => {
      entityTree.toggleProfessional(item.id);
    },
    labels: {
      singular: 'profissional',
      plural: 'profissionais',
      addLabel: 'Adicionar novo profissional',
      addPlaceholder: 'Digite o nome e pressione Enter',
      chipColor: 'primary',
      defaultTitle: 'Quais profissionais?',
      createTitle: 'Cadastre os profissionais',
      emptyWarning: 'Nenhum profissional cadastrado. Adicione um novo abaixo.',
      createInfo: 'Digite o nome do profissional para cadastrar.',
    },
  },
  service: {
    getItems: (tree) => tree.services,
    add: (entityTree, name) => {
      const id = entityTree.addService(name);
      if (id) {
        entityTree.setTree(prev => ({
          ...prev,
          services: prev.services.map(s => s.id === id ? { ...s, selected: true } : s),
        }));
      }
      return id;
    },
    toggle: (entityTree, item) => {
      entityTree.setTree(prev => ({
        ...prev,
        services: prev.services.map(s =>
          s.id === item.id ? { ...s, selected: !s.selected } : s
        ),
      }));
    },
    labels: {
      singular: 'serviço',
      plural: 'serviços',
      addLabel: 'Adicionar novo serviço',
      addPlaceholder: 'Digite o nome e pressione Enter',
      chipColor: 'secondary',
      defaultTitle: 'Quais serviços?',
      createTitle: 'Cadastre os serviços',
      emptyWarning: 'Nenhum serviço cadastrado. Adicione um novo abaixo.',
      createInfo: 'Digite o nome do serviço para cadastrar.',
    },
  },
  place: {
    getItems: (tree) => tree.places,
    add: (entityTree, name) => {
      const id = entityTree.addPlace(name);
      if (id) {
        entityTree.setTree(prev => ({
          ...prev,
          places: prev.places.map(p => p.id === id ? { ...p, selected: true } : p),
        }));
      }
      return id;
    },
    toggle: (entityTree, item) => {
      entityTree.setTree(prev => ({
        ...prev,
        places: prev.places.map(p =>
          p.id === item.id ? { ...p, selected: !p.selected } : p
        ),
      }));
    },
    labels: {
      singular: 'local',
      plural: 'locais',
      addLabel: 'Adicionar novo local',
      addPlaceholder: 'Ex: Clinica Centro, Studio Downtown...',
      chipColor: 'success',
      defaultTitle: 'Onde sera o atendimento?',
      createTitle: null, // sera gerado dinamicamente com getLabel
      emptyWarning: null, // sera gerado dinamicamente com getLabel
      createInfo: null, // sera gerado dinamicamente com getLabel
    },
  },
};

export default function EntityPickerStep({
  entityTree,
  mode = 'select-or-create',
  stepProps = {},
  getLabel,
  onValidChange,
}) {
  const [newName, setNewName] = useState('');
  const { entityType, min = 0, max = Infinity } = stepProps;

  const entityConfig = ENTITY_CONFIG[entityType];
  if (!entityConfig) {
    return (
      <Alert severity="error">
        Tipo de entidade desconhecido: "{entityType}"
      </Alert>
    );
  }

  const { tree } = entityTree;
  const items = entityConfig.getItems(tree);
  const selectedCount = items.filter(i => i.selected).length;
  const canCreate = mode === 'create-only' || mode === 'select-or-create';
  const canSelect = mode === 'select-only' || mode === 'select-or-create';

  // Labels dinamicos para "place" (usa getLabel do tema)
  const labels = useMemo(() => {
    if (entityType === 'place' && getLabel) {
      const placeSingular = getLabel('placesSingular')?.toLowerCase() || 'local';
      const placePreposition = getLabel('placesPreposition') || 'do';
      return {
        ...entityConfig.labels,
        singular: placeSingular,
        addLabel: `Nome ${placePreposition} ${placeSingular}`,
        createTitle: `Qual o nome ${placePreposition} ${placeSingular}?`,
        emptyWarning: `Nenhum ${placeSingular} cadastrado. Adicione um novo abaixo.`,
        createInfo: `Digite o nome ${placePreposition} ${placeSingular} e pressione Enter para adicionar.`,
      };
    }
    return entityConfig.labels;
  }, [entityType, getLabel, entityConfig.labels]);

  // Validacao
  useEffect(() => {
    const isValid = selectedCount >= min && selectedCount <= max;
    let validationMessage = '';

    if (!isValid) {
      if (selectedCount < min) {
        const needed = min - selectedCount;
        const label = needed === 1 ? labels.singular : labels.plural;
        validationMessage = `Selecione ${needed} ${label}${needed === 1 ? '' : 's'} para continuar`;
      } else if (selectedCount > max) {
        const label = max === 1 ? labels.singular : labels.plural;
        validationMessage = `Selecione até ${max} ${label}${max === 1 ? '' : 's'}`;
      }
    }

    onValidChange(isValid, validationMessage);
  }, [selectedCount, min, max, onValidChange, labels]);

  // Subtitulo contextual
  const contextSubtitle = useMemo(
    () => buildContextSubtitle(entityType, tree),
    [entityType, tree.professionals, tree.services, tree.places]
  );

  const handleAdd = () => {
    const name = newName.trim();
    if (!name) return;
    entityConfig.add(entityTree, name);
    setNewName('');
  };

  const handleToggle = (item) => {
    if (!canSelect && !item.isNew) return;
    if (selectedCount >= max && !item.selected) return;
    entityConfig.toggle(entityTree, item);
  };

  const title = mode === 'create-only'
    ? (labels.createTitle || `Cadastre os ${labels.plural}`)
    : (labels.defaultTitle || `Quais ${labels.plural}?`);

  // Ordenar: novos primeiro
  const sortedItems = useMemo(
    () => [...items].sort((a, b) => (b.isNew ? 1 : 0) - (a.isNew ? 1 : 0)),
    [items]
  );

  return (
    <Box sx={{ py: 2 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          {title}
        </Typography>
      </Box>

      {/* Subtitulo contextual - mostra o que ja foi selecionado nos steps anteriores */}
      {contextSubtitle && (
        <Alert severity="info" icon={false} sx={{ mb: 2 }}>
          <Typography variant="body2">{contextSubtitle}</Typography>
        </Alert>
      )}

      {/* Info para modo create-only (sem contexto ja exibido) */}
      {mode === 'create-only' && !contextSubtitle && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {labels.createInfo || `Digite o nome para cadastrar.`}
        </Alert>
      )}

      {/* Warning quando nao ha items e pode selecionar existentes */}
      {mode !== 'create-only' && items.length === 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {labels.emptyWarning || `Nenhum ${labels.singular} cadastrado. Adicione um novo abaixo.`}
        </Alert>
      )}

      {/* Lista de items */}
      {(mode !== 'create-only' || items.some(i => i.isNew)) && sortedItems.length > 0 && (
        <List dense sx={{ maxHeight: 300, overflow: 'auto', mb: 2 }}>
          {sortedItems.map((item) => (
            <ListItem
              key={item.id}
              button
              onClick={() => handleToggle(item)}
              sx={{
                borderRadius: 1,
                mb: 0.5,
                bgcolor: item.selected ? 'action.selected' : 'transparent',
                opacity: (!canSelect && !item.isNew) ? 0.5 : 1,
              }}
            >
              <ListItemIcon>
                <Checkbox
                  edge="start"
                  checked={!!item.selected}
                  tabIndex={-1}
                  disableRipple
                  disabled={!canSelect && !item.isNew}
                />
              </ListItemIcon>
              <ListItemText
                primary={item.name}
                secondary={item.isNew ? '(Novo)' : (item.category || null)}
              />
              {item.isNew && (
                <Chip
                  size="small"
                  label="Novo"
                  color={labels.chipColor}
                  variant="outlined"
                />
              )}
            </ListItem>
          ))}
        </List>
      )}

      {/* Campo para adicionar novo */}
      {canCreate && (
        <TextField
          fullWidth
          autoFocus={mode === 'create-only'}
          label={labels.addLabel}
          placeholder={labels.addPlaceholder}
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleAdd();
            }
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleAdd} edge="end" disabled={!newName.trim()}>
                  <AddIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      )}

      {selectedCount > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {pluralizeWithCount(selectedCount, labels.singular, labels.plural)} {pluralizePastParticiple(selectedCount, 'selecionado', 'selecionada', 'm')}
        </Typography>
      )}
    </Box>
  );
}
